/**
 * Economy-cluster panels (PXL): Marketplace (global book / my listings / auctions
 * / Driftwell stalls / price history) and the Atelier store (cosmetics) + Gleam
 * wallet + Cloud Caches. The marketplace rides the socket (Shard currency); the
 * store is a SEPARATE REST surface (Gleam) — the AD-024 wall is structural here.
 */

import { useEffect, useState } from 'react';

import {
  ApiError,
  acceptPrestigeOffer,
  claimHeldGrants,
  getCache,
  getHeldGrants,
  getStoreBundles,
  getStoreCatalogue,
  getStorePacks,
  getStoreWallet,
  storeBuy,
  storeCheckout,
  storePackCheckout,
  storeTopup,
  type StoreBundlesResponse,
  type StorePacksResponse,
} from '../../net/api.js';
import { useGame } from '../net-context.js';
import {
  ACCEPTED_HELD,
  ACCEPTED_OWNED,
  ACCEPT_LABEL,
  describeAcceptRefusal,
  parsePrestige,
  standingWords,
  windowWords,
} from './prestigeWords.js';
import { DataView, Empty, IconGlyph, Money, SectionTitle, Tabs } from '../widgets.js';

import {
  NEVER_REISSUED,
  NOT_TRADEABLE_AT_LAUNCH,
  describeClaimRefusal,
  describePackRefusal,
  founderChainNotice,
  founderStatusLine,
  packAction,
  packLabel,
  packGleamEquivalent,
  packGleamLine,
  packPriceLines,
} from './founderPacks.js';
import { storeModeDisclosure } from './storeMode.js';

export function MarketplacePanel(): JSX.Element {
  const { state, conn } = useGame();
  const [tab, setTab] = useState('browse');
  const [q, setQ] = useState({ contentId: '', minPrice: '', maxPrice: '' });
  const [sell, setSell] = useState({ itemId: '', askPrice: '' });
  const [auc, setAuc] = useState({ itemId: '', minBid: '' });
  const [priceId, setPriceId] = useState('');

  const search = (): void =>
    conn.send('marketSearch', {
      contentId: q.contentId || undefined,
      minPrice: q.minPrice ? Number(q.minPrice) : undefined,
      maxPrice: q.maxPrice ? Number(q.maxPrice) : undefined,
    });

  return (
    <div>
      <Tabs
        tabs={[
          { id: 'browse', label: 'Browse' },
          { id: 'sell', label: 'Sell' },
          { id: 'mine', label: 'Mine' },
          { id: 'auctions', label: 'Auctions' },
          { id: 'stalls', label: 'Stalls' },
          { id: 'price', label: 'Price' },
        ]}
        active={tab}
        onSelect={setTab}
      />

      {tab === 'browse' ? (
        <div className="pxl-list">
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              className="pxl-input grow"
              placeholder="content id"
              value={q.contentId}
              onChange={(e) => setQ({ ...q, contentId: e.target.value })}
            />
            <input
              className="pxl-input"
              style={{ width: 60 }}
              placeholder="min"
              value={q.minPrice}
              onChange={(e) => setQ({ ...q, minPrice: e.target.value })}
            />
            <input
              className="pxl-input"
              style={{ width: 60 }}
              placeholder="max"
              value={q.maxPrice}
              onChange={(e) => setQ({ ...q, maxPrice: e.target.value })}
            />
            <button className="pxl-btn" onClick={search}>
              search
            </button>
          </div>
          {state.marketSearch.length === 0 ? <Empty>No listings — search the book.</Empty> : null}
          {state.marketSearch.map((l) => (
            <div className="pxl-row" key={l.id}>
              <IconGlyph code={l.contentId} label={l.name} assetRef="TGC-UI-030" size={24} />
              <div className="grow">
                <div className={l.item ? `r-${l.item.rarity}` : ''}>{l.name}</div>
                <div className="pxl-faint">
                  {l.kind === 'resource'
                    ? `×${l.resource?.quantity} q${l.resource?.quality}`
                    : l.item?.quality}{' '}
                  · {l.sellerName}
                </div>
              </div>
              <Money amount={l.askPrice} kind="shard" />
              <button
                className="pxl-btn tiny"
                onClick={() => conn.send('marketBuy', { listingId: l.id })}
              >
                buy
              </button>
            </div>
          ))}
          <div className="pxl-faint">Stat-range filters are a documented Phase-5 deferral.</div>
        </div>
      ) : null}

      {tab === 'sell' ? (
        <div className="pxl-list">
          <SectionTitle>List an item for sale (1% fee burned)</SectionTitle>
          <input
            className="pxl-input"
            placeholder="item instance id"
            value={sell.itemId}
            onChange={(e) => setSell({ ...sell, itemId: e.target.value })}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              className="pxl-input grow"
              type="number"
              placeholder="ask price (◈)"
              value={sell.askPrice}
              onChange={(e) => setSell({ ...sell, askPrice: e.target.value })}
            />
            <button
              className="pxl-btn primary"
              disabled={!sell.itemId || !sell.askPrice}
              onClick={() =>
                conn.send('marketList', { itemId: sell.itemId, askPrice: Number(sell.askPrice) })
              }
            >
              list
            </button>
          </div>
          <div className="pxl-faint">
            The 1% listing + 5% sales tax are burned (no recipient) — a sink.
          </div>
        </div>
      ) : null}

      {tab === 'mine' ? (
        <div className="pxl-list">
          <button className="pxl-btn" onClick={() => conn.send('marketMine', {})}>
            refresh my listings
          </button>
          {state.marketMine.length === 0 ? <Empty>No active listings.</Empty> : null}
          {state.marketMine.map((l) => (
            <div className="pxl-row" key={l.id}>
              <div className="grow">{l.name}</div>
              <Money amount={l.askPrice} kind="shard" />
              <button
                className="pxl-btn tiny"
                onClick={() => conn.send('marketCancel', { listingId: l.id })}
              >
                cancel
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {tab === 'auctions' ? (
        <div className="pxl-list">
          <button className="pxl-btn" onClick={() => conn.send('auctionSearch', {})}>
            browse auctions
          </button>
          {state.auctionSearch.map((a) => (
            <div className="pxl-row" key={a.id}>
              <IconGlyph code={a.contentId} label={a.name} assetRef="TGC-UI-030" size={24} />
              <div className="grow">
                <div className={`r-${a.item.rarity}`}>{a.name}</div>
                <div className="pxl-faint">
                  bid ◈ {a.currentBid ?? a.minBid} · {a.sellerName} · ends {a.endsAt.slice(11, 16)}
                </div>
              </div>
              <button
                className="pxl-btn tiny"
                onClick={() =>
                  conn.send('auctionBid', {
                    auctionId: a.id,
                    bidAmount: (a.currentBid ?? a.minBid) + 1,
                  })
                }
              >
                bid +1
              </button>
            </div>
          ))}
          <SectionTitle>Auction an item (Epic+ / Masterwork)</SectionTitle>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              className="pxl-input grow"
              placeholder="item id"
              value={auc.itemId}
              onChange={(e) => setAuc({ ...auc, itemId: e.target.value })}
            />
            <input
              className="pxl-input"
              type="number"
              style={{ width: 80 }}
              placeholder="min bid"
              value={auc.minBid}
              onChange={(e) => setAuc({ ...auc, minBid: e.target.value })}
            />
            <button
              className="pxl-btn"
              disabled={!auc.itemId || !auc.minBid}
              onClick={() =>
                conn.send('auctionCreate', { itemId: auc.itemId, minBid: Number(auc.minBid) })
              }
            >
              create
            </button>
          </div>
        </div>
      ) : null}

      {tab === 'stalls' ? (
        <div className="pxl-list">
          <button className="pxl-btn" onClick={() => conn.send('stallSearch', {})}>
            browse Driftwell stalls
          </button>
          {state.stallSearch.map((s) => (
            <div className="pxl-row" key={s.id}>
              <IconGlyph code={s.contentId} label={s.name} assetRef="TGC-PRP-026" size={24} />
              <div className="grow">
                <div>{s.name}</div>
                <div className="pxl-faint">{s.renterName} · 2% tax</div>
              </div>
              <Money amount={s.askPrice} kind="shard" />
              <button
                className="pxl-btn tiny"
                onClick={() => conn.send('stallBuy', { stallId: s.id })}
              >
                buy
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {tab === 'price' ? (
        <div className="pxl-list">
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              className="pxl-input grow"
              placeholder="content id"
              value={priceId}
              onChange={(e) => setPriceId(e.target.value)}
            />
            <button
              className="pxl-btn"
              disabled={!priceId}
              onClick={() => conn.send('priceHistoryRequest', { contentId: priceId, days: 14 })}
            >
              history
            </button>
          </div>
          {state.priceHistory ? (
            <div className="pxl-list">
              {state.priceHistory.days.map((d) => (
                <div className="pxl-stat" key={d.day}>
                  <span className="k">{d.day}</span>
                  <span className="v">
                    {d.low}–{d.high} (med {d.median}, vol {d.volume})
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <Empty>Diagnostic price aggregates (never a Scarcity input).</Empty>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** AD-332 store copy (design §5.8 item 7): human words for the gate's refusal, never the raw code. */
function storeErrText(e: unknown): string {
  if (e instanceof ApiError && e.code === 'emailUnverified')
    return 'Verify your email address before purchasing — check your inbox, or use Account → resend verification.';
  return String(e);
}

/**
 * The prestige lines (AD-779 S3): the window's words, the caller's own standing and — only
 * for a live offer to the caller — one Accept control. Never a price, a Buy control, a queue
 * number or a countdown on the window (prestige-cosmetic-acquisition-2026-10.md §4 item 6, §11).
 */
function PrestigeSection({
  catalogue,
  onChanged,
}: {
  catalogue: unknown;
  onChanged: () => void;
}): JSX.Element | null {
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const lines = parsePrestige(catalogue);
  if (lines.length === 0) return null;
  const accept = (cosmeticId: string): void => {
    setBusy(true);
    void acceptPrestigeOffer(cosmeticId)
      .then((r) => {
        setMsg(r.held ? ACCEPTED_HELD : ACCEPTED_OWNED);
        onChanged();
      })
      .catch((e: unknown) => {
        setMsg(e instanceof ApiError ? describeAcceptRefusal(e.code) : String(e));
        // A refused offer (lapsed, gone) must not keep its Accept control on screen.
        onChanged();
      })
      .finally(() => setBusy(false));
  };
  return (
    <div className="pxl-list" data-testid="prestige-lines">
      <SectionTitle>Prestige</SectionTitle>
      {msg ? <div className="pxl-row">{msg}</div> : null}
      {lines.map((p) => (
        <div className="pxl-list" key={p.id} data-prestige-line={p.id}>
          <div className="pxl-row">
            <div className="grow">{p.name}</div>
            <div className="pxl-faint">{windowWords(p)}</div>
          </div>
          <div className="pxl-faint">{standingWords(p)}</div>
          {p.offered && !p.owned && !p.held ? (
            <button className="pxl-btn" disabled={busy} onClick={() => accept(p.id)}>
              {ACCEPT_LABEL}
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function StorePanel(): JSX.Element {
  const { state, store } = useGame();
  const [tab, setTab] = useState('atelier');
  const [err, setErr] = useState('');
  /** AD-332 item 3: the server suggested enrolling a stronger MFA tier (mfaPrompt on a purchase response). */
  const [mfaNudge, setMfaNudge] = useState(false);

  const [bundles, setBundles] = useState<StoreBundlesResponse | null>(null);
  // UK/EU express consent (payments-stripe.md §2a): one box worded to the
  // stricter reading covers both assertions; checkout is refused without it.
  const [consented, setConsented] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    void getStoreCatalogue()
      .then((c) => store.setStoreCatalogue(c))
      .catch((e: unknown) => setErr(String(e)));
    void getStoreWallet()
      .then((w) => store.setGleamWallet(w))
      .catch(() => undefined);
    void getStoreBundles()
      .then((b) => setBundles(b))
      .catch(() => undefined);
    // Checkout return (payments-stripe.md §6): after the hosted page redirects
    // back, refresh the wallet a few times while the webhook credit lands.
    const timers: number[] = [];
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('store');
    if (outcome === 'success' || outcome === 'cancel') {
      params.delete('store');
      const query = params.toString();
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${query ? `?${query}` : ''}`,
      );
      if (outcome === 'success') {
        setNotice('payment received — Gleam arriving shortly');
        for (const ms of [2000, 5000, 10000]) {
          timers.push(
            window.setTimeout(() => {
              void getStoreWallet()
                .then((w) => store.setGleamWallet(w))
                .catch(() => undefined);
            }, ms),
          );
        }
      } else {
        setNotice('checkout cancelled — nothing was charged');
      }
    }
    return () => {
      for (const t of timers) window.clearTimeout(t);
    };
  }, [store]);

  return (
    <div>
      <Tabs
        tabs={[
          { id: 'atelier', label: 'Atelier' },
          { id: 'wallet', label: 'Gleam' },
          { id: 'caches', label: 'Cloud Caches' },
          { id: 'founder', label: 'Founder packs' },
        ]}
        active={tab}
        onSelect={setTab}
      />
      <div className="pxl-faint" style={{ marginBottom: 6 }}>
        Cosmetic-only (Pillar 4/5). Gleam never converts to Shard (AD-024 wall).
        {/* The stub disclosure must never render once a real provider is live
            (GATE-2026-07-15-stripe-payments CF-3): claim it ONLY when the server
            reports stub mode — loading/unknown says nothing rather than lying. */}
        {storeModeDisclosure(bundles?.mode)}
      </div>
      {err ? <div className="pxl-bad">{err}</div> : null}
      {mfaNudge ? (
        <div className="pxl-faint">
          Protect your purchases — enable two-factor authentication under Account → Two-factor
          authentication.
        </div>
      ) : null}

      {tab === 'atelier' ? (
        <div className="pxl-list">
          <PrestigeSection
            catalogue={state.storeCatalogue}
            onChanged={() =>
              void getStoreCatalogue()
                .then((c) => store.setStoreCatalogue(c))
                .catch((e: unknown) => setErr(String(e)))
            }
          />
          <SectionTitle>Catalogue</SectionTitle>
          {state.storeCatalogue ? (
            <DataView value={state.storeCatalogue} />
          ) : (
            <Empty>Loading…</Empty>
          )}
          <div className="pxl-faint">
            Buy via POST /api/store/buy (cosmetic id). Display-only fiat label.
          </div>
        </div>
      ) : null}

      {tab === 'wallet' ? (
        <div className="pxl-list">
          <div className="pxl-row">
            <div className="grow">Gleam wallet</div>
            <Money
              amount={
                state.gleamWallet &&
                typeof state.gleamWallet === 'object' &&
                'balance' in state.gleamWallet
                  ? (state.gleamWallet as { balance: number }).balance
                  : undefined
              }
              kind="gleam"
            />
          </div>
          {notice ? <div className="pxl-row">{notice}</div> : null}
          <div className="pxl-row">
            <div className="grow">
              buy Gleam{bundles?.mode === 'stub' ? ' (stub — dev instant credit)' : ''}
            </div>
          </div>
          {bundles?.mode === 'checkout' && bundles.checkoutConsent ? (
            <label className="pxl-row" style={{ alignItems: 'flex-start', gap: 6 }}>
              <input
                type="checkbox"
                checked={consented}
                onChange={(e) => setConsented(e.currentTarget.checked)}
              />
              <span className="pxl-faint">
                {bundles.checkoutConsent.wording.immediateSupply}{' '}
                {bundles.checkoutConsent.wording.withdrawalLoss}
              </span>
            </label>
          ) : null}
          {(bundles?.bundles ?? []).map((b) => (
            <div className="pxl-row" key={b.id}>
              <div className="grow">
                {b.gleam.toLocaleString()} Gleam — {b.displayPrice}
              </div>
              <button
                className="pxl-btn"
                onClick={() => {
                  if (bundles?.mode === 'checkout') {
                    // Hosted Stripe Checkout (payments-stripe.md §2): the page
                    // redirects back with ?store=success|cancel handled above.
                    // §2a: the server refuses consentRequired until the express-
                    // consent box above is ticked; surface that plainly here.
                    if (!consented) {
                      setErr('tick the consent box above to continue to checkout');
                      return;
                    }
                    void storeCheckout(b.id, consented)
                      .then((r) => {
                        if ((r as { mfaPrompt?: boolean }).mfaPrompt) setMfaNudge(true);
                        window.location.assign(r.url);
                      })
                      .catch((e: unknown) => setErr(storeErrText(e)));
                  } else {
                    void storeTopup(b.id)
                      .then((w) => {
                        if ((w as { mfaPrompt?: boolean }).mfaPrompt) setMfaNudge(true);
                        store.setGleamWallet(w);
                      })
                      .catch((e: unknown) => setErr(storeErrText(e)));
                  }
                }}
              >
                {bundles?.mode === 'checkout' ? 'buy' : 'credit (stub)'}
              </button>
            </div>
          ))}
          <DataView value={state.gleamWallet} />
        </div>
      ) : null}

      {tab === 'founder' ? <FounderPacksTab onMfaPrompt={() => setMfaNudge(true)} /> : null}

      {tab === 'caches' ? (
        <div className="pxl-list">
          <div className="pxl-faint">
            Published odds, no-dup draw, pity counter (AD-024). Founder/scarcity-target items are
            barred from every pool.
          </div>
          <button
            className="pxl-btn"
            onClick={() =>
              void getCache('starter')
                .then((c) => store.setStoreCatalogue(c))
                .catch((e: unknown) => setErr(String(e)))
            }
          >
            inspect cache "starter"
          </button>
          <button
            className="pxl-btn"
            disabled
            title="needs Gleam + region compliance"
            onClick={() => void storeBuy('')}
          >
            open (region-gated)
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The founder-pack shelf + the E3 claim step (founder-packs.md §3a, §4a, §7; AD-661 F2 slice 11).
 * Every price and every quote is the server's; the upgrade credit is shown as a prior purchase,
 * never as a discount (§3a item 3). Nothing renders as purchasable unless the server lists it.
 */
function FounderPacksTab({ onMfaPrompt }: { onMfaPrompt: () => void }): JSX.Element {
  const [shelf, setShelf] = useState<StorePacksResponse | null>(null);
  const [held, setHeld] = useState<Array<{ cosmeticIds: string[]; at: string }>>([]);
  const [consented, setConsented] = useState(false);
  const [msg, setMsg] = useState('');

  const load = (): void => {
    void getStorePacks()
      .then((r) => setShelf(r))
      .catch((e: unknown) => setMsg(String(e)));
    void getHeldGrants()
      .then((r) => setHeld(r.held))
      .catch(() => undefined);
  };
  useEffect(load, []);

  const checkout = (packId: string, upgrade: boolean): void => {
    if (!consented) {
      setMsg(describePackRefusal('consentRequired'));
      return;
    }
    void storePackCheckout(packId, upgrade, consented)
      .then((r) => {
        if (r.mfaPrompt) onMfaPrompt();
        window.location.assign(r.url);
      })
      .catch((e: unknown) =>
        setMsg(e instanceof ApiError ? describePackRefusal(e.code) : String(e)),
      );
  };

  const claim = (): void => {
    void claimHeldGrants()
      .then((r) => {
        setMsg(`Claimed ${r.claimed.length} item(s).`);
        load();
      })
      .catch((e: unknown) =>
        setMsg(e instanceof ApiError ? describeClaimRefusal(e.code) : String(e)),
      );
  };

  const status = founderStatusLine(shelf?.founder ?? null);
  const chainNotice = founderChainNotice(shelf?.founder ?? null);
  const heldIds = held.flatMap((g) => g.cosmeticIds);

  return (
    <div className="pxl-list">
      {msg ? <div className="pxl-row">{msg}</div> : null}
      {status ? <div className="pxl-row pxl-good">{status}</div> : null}
      {chainNotice ? <div className="pxl-row pxl-faint">{chainNotice}</div> : null}
      {heldIds.length > 0 ? (
        <div className="pxl-list">
          <SectionTitle>Claim your Firstlight items</SectionTitle>
          <div className="pxl-faint">
            {heldIds.length} item(s) are waiting: {heldIds.join(', ')}. Founder items need
            two-factor authentication on the account before they can be held.
          </div>
          <button className="pxl-btn" onClick={claim}>
            claim
          </button>
        </div>
      ) : null}
      {!shelf ? <Empty>Loading…</Empty> : null}
      {shelf && shelf.packs.length === 0 ? <Empty>No founder pack is on sale.</Empty> : null}
      {shelf && shelf.packs.length > 0 && shelf.mode === 'checkout' && shelf.checkoutConsent ? (
        <label className="pxl-row" style={{ alignItems: 'flex-start', gap: 6 }}>
          <input
            type="checkbox"
            checked={consented}
            onChange={(e) => setConsented(e.currentTarget.checked)}
          />
          <span className="pxl-faint">
            {shelf.checkoutConsent.wording.immediateSupply}{' '}
            {shelf.checkoutConsent.wording.withdrawalLoss}
          </span>
        </label>
      ) : null}
      {(shelf?.packs ?? []).map((p) => {
        const action = packAction(p, shelf?.founder ?? null);
        const gleamLine = packGleamLine(p);
        return (
          <div className="pxl-list" key={p.id}>
            <SectionTitle>{packLabel(p.id)}</SectionTitle>
            <div className="pxl-faint">
              {NEVER_REISSUED} {NOT_TRADEABLE_AT_LAUNCH}
              {p.closesAt ? ` On sale until ${new Date(p.closesAt).toLocaleDateString()}.` : ''}
            </div>
            {p.cosmetics.map((c) => (
              <div className="pxl-row" key={c.id}>
                <div className="grow">{c.name}</div>
                <div className="pxl-faint">{c.kind}</div>
              </div>
            ))}
            {gleamLine.gleam > 0 ? (
              <div className="pxl-row">
                <div className="grow">{gleamLine.label}</div>
                <Money amount={gleamLine.gleam} kind="gleam" />
                <div className="pxl-faint">{packGleamEquivalent(p)}</div>
              </div>
            ) : null}
            {packPriceLines(p).map((l) => (
              <div className="pxl-row" key={l.label}>
                <div className="grow">{l.label}</div>
                <div>{l.amount}</div>
              </div>
            ))}
            {shelf?.mode !== 'checkout' ? (
              <div className="pxl-faint">Checkout is not available on this server.</div>
            ) : action.kind === 'buy' ? (
              <button className="pxl-btn" onClick={() => checkout(p.id, false)}>
                buy
              </button>
            ) : action.kind === 'upgrade' ? (
              <button className="pxl-btn" onClick={() => checkout(p.id, true)}>
                upgrade
              </button>
            ) : action.kind === 'held' ? (
              <div className="pxl-faint">Held.</div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
